// Filter Drawer JS - v3 with Price Range Support
class FilterDrawer {
  constructor() {
    this.selectors = {
      drawer: '.filter-drawer',
      toggle: '.filter-drawer-toggle',
      close: '.filter-drawer__close',
      overlay: '.filter-drawer__overlay',
      checkbox: '.filter-drawer .tag__input',
      form: '.filter-drawer__form',
      priceRange: '.price-range',
      clearBtn: '.filter-drawer__clear',
      applyBtn: '.filter-drawer__apply',
      activeFilters: '.tag-list--active-tags',
      filterGroups: '.collection-filter__group'
    };
    
    this.elements = {
      drawer: document.querySelector(this.selectors.drawer),
      toggle: document.querySelector(this.selectors.toggle),
      close: null,
      overlay: null,
      form: null,
      body: document.body
    };
    
    this.isOpen = false;
    this.activeFilters = new Set();
    
    this.init();
  }

  init() {
    if (!this.elements.drawer || !this.elements.toggle) return;
    
    // Cache elements
    this.elements.close = this.elements.drawer.querySelector(this.selectors.close);
    this.elements.overlay = this.elements.drawer.querySelector(this.selectors.overlay);
    this.elements.form = this.elements.drawer.querySelector(this.selectors.form);
    
    // Setup event listeners
    this.bindEvents();
    
    // Initialize features
    this.syncFiltersWithPage();
    this.initPriceRangeSliders();
    this.updateFilterCount();
    this.updateActiveFiltersList();
  }

  bindEvents() {
    // Toggle drawer
    this.elements.toggle?.addEventListener('click', () => this.open());
    
    // Close drawer
    this.elements.close?.addEventListener('click', () => this.close());
    this.elements.overlay?.addEventListener('click', () => this.close());
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    
    // Handle checkbox changes
    const checkboxes = this.elements.drawer.querySelectorAll(this.selectors.checkbox);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleFilterChange(e));
    });
    
    // Clear filters
    const clearBtn = this.elements.drawer.querySelector(this.selectors.clearBtn);
    clearBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.clearAllFilters();
    });
    
    // Apply filters
    const applyBtn = this.elements.drawer.querySelector(this.selectors.applyBtn);
    applyBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.applyFilters();
    });
    
    // Handle active filter removal
    this.bindActiveFilterEvents();
  }

  open() {
    this.isOpen = true;
    this.elements.drawer.classList.add('is-open');
    this.elements.body.classList.add('overflow-hidden');
    
    // Trap focus
    this.elements.drawer.setAttribute('aria-hidden', 'false');
    this.elements.close?.focus();
  }

  close() {
    this.isOpen = false;
    this.elements.drawer.classList.remove('is-open');
    this.elements.body.classList.remove('overflow-hidden');
    
    // Release focus
    this.elements.drawer.setAttribute('aria-hidden', 'true');
    this.elements.toggle?.focus();
  }

  handleFilterChange(event) {
    const checkbox = event.target;
    const filterValue = checkbox.value;
    const filterName = checkbox.name;
    
    if (checkbox.checked) {
      this.activeFilters.add(`${filterName}:${filterValue}`);
    } else {
      this.activeFilters.delete(`${filterName}:${filterValue}`);
    }
    
    // Update UI
    this.updateFilterCount();
    this.updateActiveFiltersList();
  }

  syncFiltersWithPage() {
    // Get current filters from URL or page state
    const urlParams = new URLSearchParams(window.location.search);
    const currentFilters = new Map();
    
    // Parse URL parameters
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.')) {
        const values = value.split(',');
        values.forEach(v => {
          this.activeFilters.add(`${key}:${v}`);
        });
      }
    }
    
    // Check checkboxes based on active filters
    const checkboxes = this.elements.drawer.querySelectorAll(this.selectors.checkbox);
    checkboxes.forEach(checkbox => {
      const filterKey = `${checkbox.name}:${checkbox.value}`;
      if (this.activeFilters.has(filterKey)) {
        checkbox.checked = true;
        checkbox.closest('.tag')?.classList.add('tag--active');
      }
    });
  }

  updateFilterCount() {
    const filterToggle = this.elements.toggle;
    if (!filterToggle) return;
    
    // Count checked filters
    const checkedCount = this.elements.drawer.querySelectorAll(`${this.selectors.checkbox}:checked`).length;
    
    // Count price ranges
    let priceRangeCount = 0;
    const priceRanges = this.elements.drawer.querySelectorAll(this.selectors.priceRange);
    priceRanges.forEach(range => {
      const min = parseFloat(range.dataset.minValue || range.dataset.min);
      const max = parseFloat(range.dataset.maxValue || range.dataset.max);
      const currentMin = parseFloat(range.querySelector('.price-range__input-min')?.value || min);
      const currentMax = parseFloat(range.querySelector('.price-range__input-max')?.value || max);
      
      if (currentMin !== min || currentMax !== max) {
        priceRangeCount++;
      }
    });
    
    const count = checkedCount + priceRangeCount;
    
    // Update count display
    let countElement = filterToggle.querySelector('.filter-count');
    
    if (count > 0) {
      if (!countElement) {
        countElement = document.createElement('span');
        countElement.className = 'filter-count';
        filterToggle.appendChild(countElement);
      }
      countElement.textContent = `(${count})`;
    } else if (countElement) {
      countElement.remove();
    }
  }

  initPriceRangeSliders() {
    const priceRanges = this.elements.drawer.querySelectorAll(this.selectors.priceRange);
    
    priceRanges.forEach(priceRange => {
      const sliderWrapper = priceRange.querySelector('.price-range__slider-wrapper');
      if (!sliderWrapper || !window.noUiSlider) return;
      
      // Get values from data attributes
      const min = parseFloat(priceRange.dataset.min) || 0;
      const max = parseFloat(priceRange.dataset.max) || 1000;
      const currentMin = parseFloat(priceRange.dataset.minValue) || min;
      const currentMax = parseFloat(priceRange.dataset.maxValue) || max;
      
      // Create a new slider container
      const newSlider = document.createElement('div');
      newSlider.className = 'price-range__slider noUi-target';
      
      // Clear wrapper and add new slider
      sliderWrapper.innerHTML = '';
      sliderWrapper.appendChild(newSlider);
      
      // Initialize the slider
      window.noUiSlider.create(newSlider, {
        start: [currentMin, currentMax],
        connect: true,
        range: {
          'min': min,
          'max': max
        },
        step: 1,
        format: {
          to: function (value) {
            return Math.round(value);
          },
          from: function (value) {
            return Number(value);
          }
        }
      });
      
      // Get display elements
      const minDisplay = priceRange.querySelector('.price-range__display-min');
      const maxDisplay = priceRange.querySelector('.price-range__display-max');
      
      // Update display values
      newSlider.noUiSlider.on('update', (values, handle) => {
        const minVal = values[0];
        const maxVal = values[1];
        
        // Format as currency (assuming values are in dollars/pounds)
        if (minDisplay) {
          minDisplay.textContent = this.formatPrice(minVal * 100);
        }
        if (maxDisplay) {
          maxDisplay.textContent = this.formatPrice(maxVal * 100);
        }
        
        // Update data attributes
        priceRange.dataset.minValue = minVal;
        priceRange.dataset.maxValue = maxVal;
      });
      
      // Update hidden inputs
      const minInput = priceRange.querySelector('.price-range__input-min');
      const maxInput = priceRange.querySelector('.price-range__input-max');
      
      newSlider.noUiSlider.on('change', (values, handle) => {
        if (minInput) {
          minInput.value = Math.round(values[0]);
          minInput.removeAttribute('readonly');
        }
        if (maxInput) {
          maxInput.value = Math.round(values[1]);
          maxInput.removeAttribute('readonly');
        }
        this.updateFilterCount();
      });
      
      // Set initial display values
      if (minDisplay) {
        minDisplay.textContent = this.formatPrice(currentMin * 100);
      }
      if (maxDisplay) {
        maxDisplay.textContent = this.formatPrice(currentMax * 100);
      }
    });
  }

  formatPrice(cents) {
    // Try to use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback to simple formatting
    const amount = (cents / 100).toFixed(2);
    
    // Try to determine currency symbol from existing page
    const currencySymbol = document.querySelector('.money')?.textContent.match(/[£$€¥₹]/)?.[0] || '$';
    
    return currencySymbol + amount;
  }

  updateActiveFiltersList() {
    const activeContainer = document.querySelector(this.selectors.activeFilters);
    if (!activeContainer) return;
    
    // Clear existing
    activeContainer.innerHTML = '';
    
    // Add active filters
    this.activeFilters.forEach(filter => {
      const [name, value] = filter.split(':');
      const tag = document.createElement('li');
      tag.className = 'tag--remove';
      tag.innerHTML = `
        <a href="#" data-filter="${filter}">
          ${value}
          <svg class="icon icon-close" viewBox="0 0 20 20">
            <path d="M11.414 10l4.293-4.293a1 1 0 1 0-1.414-1.414L10 8.586 5.707 4.293a1 1 0 0 0-1.414 1.414L8.586 10l-4.293 4.293a1 1 0 0 0 1.414 1.414L10 11.414l4.293 4.293a1 1 0 0 0 1.414-1.414L11.414 10z"/>
          </svg>
        </a>
      `;
      activeContainer.appendChild(tag);
    });
    
    // Rebind events
    this.bindActiveFilterEvents();
  }

  bindActiveFilterEvents() {
    const removeButtons = document.querySelectorAll('.tag--remove a');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const filter = button.dataset.filter;
        this.removeFilter(filter);
      });
    });
  }

  removeFilter(filter) {
    this.activeFilters.delete(filter);
    
    // Uncheck checkbox
    const [name, value] = filter.split(':');
    const checkbox = this.elements.drawer.querySelector(`input[name="${name}"][value="${value}"]`);
    if (checkbox) {
      checkbox.checked = false;
      checkbox.closest('.tag')?.classList.remove('tag--active');
    }
    
    // Update UI
    this.updateFilterCount();
    this.updateActiveFiltersList();
    
    // Apply filters
    this.applyFilters();
  }

  clearAllFilters() {
    // Clear checkboxes
    const checkboxes = this.elements.drawer.querySelectorAll(`${this.selectors.checkbox}:checked`);
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
      checkbox.closest('.tag')?.classList.remove('tag--active');
    });
    
    // Reset price ranges
    const priceRanges = this.elements.drawer.querySelectorAll(this.selectors.priceRange);
    priceRanges.forEach(range => {
      const slider = range.querySelector('.noUi-target');
      if (slider && slider.noUiSlider) {
        const min = parseFloat(range.dataset.min);
        const max = parseFloat(range.dataset.max);
        slider.noUiSlider.set([min, max]);
      }
    });
    
    // Clear active filters
    this.activeFilters.clear();
    
    // Update UI
    this.updateFilterCount();
    this.updateActiveFiltersList();
  }

  applyFilters() {
    // Submit the form or update the page
    const form = this.elements.form;
    if (form) {
      // For now, just submit the form
      // You can customize this to use AJAX instead
      form.submit();
    }
    
    this.close();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FilterDrawer();
  });
} else {
  new FilterDrawer();
}